import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UnrecognisedError } from '../../api/issues';
import { ago, dateAgo, dossierLink } from './issueFormat';

export interface IUnrecognisedPanelProps {
    errors: UnrecognisedError[] | null;
    loading: boolean;
    error: string | null;
    onLoad: () => void;
    /** Shows the issues of one kind of error in the backlog. */
    onShowIssues: (code: string) => void;
}

/** "customer/environment/ObjectType/id" back into its parts. */
const parseExample = (example: string) => {
    const [customer, environment, objectType, ...rest] = example.split('/');

    return { customer, environment, objectType, id: rest.join('/') };
};

/**
 * The errors no interpretation rule knows yet, across every customer, grouped by what they
 * say. Per error: the issues it is in, an example dossier, and the error as JSON for the
 * c2e-research-error skill, which turns it into a rule, a customer text and tests.
 */
export const UnrecognisedPanel = ({ errors, loading, error, onLoad, onShowIssues }: IUnrecognisedPanelProps) => {
    const [open, setOpen] = useState<string | null>(null);
    const [copied, setCopied] = useState<string | null>(null);

    const download = () => {
        if (!errors) {
            return;
        }

        const blob = new Blob([JSON.stringify(errors, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `unknown-errors-${new Date().toISOString().slice(0, 10)}.json`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const copy = (u: UnrecognisedError) => {
        navigator.clipboard
            ?.writeText(JSON.stringify([u], null, 2))
            .then(() => {
                setCopied(u.code);
                window.setTimeout(() => setCopied(null), 2000);
            })
            .catch(() => setCopied(null));
    };

    return (
        <section className="issues__section" aria-label="Onbekende fouten">
            <div className="issues__notice-head">
                <h3 className="issues__heading">
                    <FontAwesomeIcon icon="lightbulb" /> Onbekende fouten
                    {errors && <span className="issues__muted"> · {errors.length}</span>}
                </h3>
                <div className="issues__actions">
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onLoad} disabled={loading}>
                        <FontAwesomeIcon icon="rotate" spin={loading} /> Vernieuwen
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={download}
                        disabled={!errors || errors.length === 0}
                        title="Alle onbekende fouten, voor de skill c2e-research-error"
                    >
                        <FontAwesomeIcon icon="file-code" /> Alles downloaden (JSON)
                    </button>
                </div>
            </div>

            <div className="issues__howto">
                <strong>Zo maak je van een onbekende fout een bekende</strong>
                <ol>
                    <li>Download de lijst, of kopieer één fout met de knop bij die fout.</li>
                    <li>
                        Geef hem in Claude Code aan de skill <code>c2e-research-error</code> ("leer het dashboard deze fout"). Die zoekt de
                        oorzaak in de code en schrijft de uitleg, een klanttekst als de klant iets moet doen, en tests.
                    </li>
                    <li>Na de release van het dashboard zet de volgende scan de issues vanzelf onder de nieuwe uitleg.</li>
                </ol>
            </div>

            {error && <p className="issues__error">{error}</p>}
            {loading && !errors && <p className="issues__muted">Laden…</p>}

            {errors && errors.length === 0 && (
                <p className="issues__muted">
                    <FontAwesomeIcon icon="circle-check" /> Elke fout van het afgelopen jaar wordt herkend.
                </p>
            )}

            {errors && errors.length > 0 && (
                <ul className="issues__groups">
                    {errors.map((u) => {
                        const isOpen = open === u.code;
                        const example = u.examples.length > 0 ? parseExample(u.examples[0]) : null;

                        return (
                            <li key={u.code} className="unknown">
                                <div className="unknown__head">
                                    <button type="button" className="unknown__toggle" onClick={() => setOpen(isOpen ? null : u.code)} aria-expanded={isOpen}>
                                        <FontAwesomeIcon icon={isOpen ? 'angle-down' : 'angle-right'} />
                                        <span className="issues__pattern">{u.pattern}</span>
                                    </button>
                                    <span className="unknown__counts">
                                        {u.open > 0 && <strong>{u.open} open</strong>}
                                        <span>Objecten: {u.objects}</span>
                                        <span>Omgevingen: {u.environments.length}</span>
                                        <span title={dateAgo(u.lastFailedAt)}>Laatste fout: {ago(u.lastFailedAt) || '–'}</span>
                                    </span>
                                </div>

                                <div className="issues__actions">
                                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => onShowIssues(u.code)}>
                                        <FontAwesomeIcon icon="list-ul" /> Issues bekijken
                                    </button>
                                    {example && (
                                        <a
                                            className="btn btn-sm btn-outline-secondary"
                                            href={dossierLink(example.customer, example.environment, example.id)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <FontAwesomeIcon icon="arrow-up-right-from-square" /> Voorbeeld-dossier
                                        </a>
                                    )}
                                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => copy(u)} title="Deze fout als JSON, voor de skill">
                                        <FontAwesomeIcon icon="copy" /> {copied === u.code ? 'Gekopieerd' : 'Kopieer voor de skill'}
                                    </button>
                                </div>

                                {isOpen && (
                                    <div className="issues__detail">
                                        <span className="issues__muted">
                                            {u.errorType} · {u.objectTypes.join(', ')} · {u.environments.join(', ')}
                                        </span>
                                        {u.locations.length > 0 && (
                                            <span className="issues__muted">
                                                Plek in de code: <code>{u.locations.join(' · ')}</code>
                                            </span>
                                        )}
                                        {u.samples.map((sample) => (
                                            <pre key={sample} className="issues__raw">
                                                {sample}
                                            </pre>
                                        ))}
                                        {u.examples.length > 1 && (
                                            <ul className="unknown__examples">
                                                {u.examples.map((raw) => {
                                                    const e = parseExample(raw);

                                                    return (
                                                        <li key={raw}>
                                                            <a href={dossierLink(e.customer, e.environment, e.id)} target="_blank" rel="noopener noreferrer">
                                                                {e.customer} / {e.environment} · {e.objectType} {e.id}
                                                            </a>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
};
